import React, { useEffect, useState, useMemo } from 'react';
import { FormDialog, InputField, MuiBox } from '@kis-technologies/kis-ui';
import { useKioskFormStyles } from '../styled';
import { VenueFormData, VenueUpdateFormData, Mode } from '../../../types';
import { INVALID, VALID } from '../../../constants';
import { venueValidationSchema } from '../../../utils';

interface Props {
  open: boolean;
  mode: Mode;
  initialData?: VenueUpdateFormData;
  onClose: () => void;
  onSubmit: (data: VenueFormData) => void;
}

export const VenueForm: React.FC<Props> = ({ open, mode, initialData, onClose, onSubmit }) => {
  const isEdit = mode === 'edit';
  const classes = useKioskFormStyles();

  const [formData, setFormData] = useState<VenueFormData>({
    name: '',
    address: ''
  });
  const [validation, setValidation] = useState<{ [x: string]: string }>({});
  const [validationMessage, setValidationMessage] = useState<{ [x: string]: string | null }>({});

  /**
   * PREFILL DATA IN EDIT MODE
   */
  useEffect(() => {
    if (isEdit && initialData) {
      setFormData({
        name: initialData.name || '',
        address: initialData.address || ''
      });
    }
  }, [initialData, isEdit]);

  /**
   * INPUT CHANGE
   */
  const updateField = (key: keyof VenueFormData, value: string) => {
    if (validation[key] === INVALID) {
      setValidation({ ...validation, [key]: VALID });
      setValidationMessage({ ...validationMessage, [key]: null });
    }

    setFormData({
      ...formData,
      [key]: value
    });
  };
  /**
   * INPUT BLUR VALIDATION
   */
  const onInputBlur = async (
    key: keyof VenueFormData,
    e: React.ChangeEvent<HTMLInputElement>,
    value: string | null = null
  ) => {
    e.preventDefault();

    const trimmed = e.target.value.trim();

    setFormData({
      ...formData,
      [key]: trimmed
    });

    let invalidMessage = null;

    if (value && value.trim().length) {
      const result = await venueValidationSchema.validate({ [key]: value.trim() }).catch((err) => err);

      invalidMessage = result?.message;
    }

    setValidation({
      ...validation,
      [key]: invalidMessage ? INVALID : VALID
    });

    setValidationMessage({
      ...validationMessage,
      [key]: invalidMessage
    });
  };

  /**
   * HELPER TEXT
   */
  const validate = (key: keyof VenueFormData) => validationMessage[key];

  /**
   * DISABLE SUBMIT CONDITIONS
   */
  const hasEmptyInput = useMemo(() => !formData.name.trim() || !formData.address.trim(), [formData]);

  const hasInvalidInput = useMemo(() => Object.values(validation).includes(INVALID), [validation]);

  const buttonDisabled = hasEmptyInput || hasInvalidInput;

  /**
   * SUBMIT FORM
   */
  const handleSubmit = () => {
    const payload: VenueFormData = {
      name: formData.name.trim(),
      address: formData.address.trim()
    };

    onSubmit(payload);
  };

  return (
    <FormDialog
      open={open}
      title={isEdit ? 'Edit Venue' : 'Create Venue'}
      confirmButtonText={isEdit ? 'Save' : 'Create'}
      cancelButtonText="Cancel"
      cancelButtonAction={onClose}
      confirmButtonAction={handleSubmit}
      confirmButtonDisabled={buttonDisabled}
    >
      <MuiBox mt="14px">
        {/* NAME */}
        <MuiBox className={classes.formOverlayInputBox}>
          <InputField
            label="Name"
            size="small"
            sx={{ width: '524px', height: '45px' }}
            fullWidth
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            onBlur={(e) => onInputBlur('name', e, e.target.value)}
            helperText={validate('name')}
            error={validation.name === INVALID}
          />
        </MuiBox>

        {/* ADDRESS */}
        <MuiBox className={classes.formOverlayInputBox}>
          <InputField
            label="Address"
            size="small"
            sx={{ width: '524px', height: '45px' }}
            fullWidth
            value={formData.address}
            onChange={(e) => updateField('address', e.target.value)}
            onBlur={(e) => onInputBlur('address', e, e.target.value)}
            helperText={validate('address')}
            error={validation.address === INVALID}
          />
        </MuiBox>
      </MuiBox>
    </FormDialog>
  );
};
